import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { PERMISSIONS } from '@nks/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Segmented, Tag, Typography, message } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { ApiError } from '../../lib/api-client';
import { formatDateTime } from '../../lib/format';
import { useDebounced } from '../../lib/use-debounced';
import { useHasPermission } from '../auth/session';
import { exportCasesCsv, listCases, type ListCasesParams, type ServiceCase } from './api';
import { severityColour, severityLabel, statusColour, statusLabel } from './labels';
import { NewCaseDrawer } from './NewCaseDrawer';

const { Text } = Typography;

type View = 'mine' | 'overdue' | 'open';

const isOverdue = (c: ServiceCase): boolean => c.breached.response || c.breached.resolve;

/** The service advisor's queue: my cases first, overdue one tap away. */
export function CasesPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canManage = useHasPermission(PERMISSIONS.CASE_MANAGE);
  const [view, setView] = useState<View>('mine');
  const [search, setSearch] = useState('');
  const q = useDebounced(search, 250);
  // The customer page's New case lands here with ?new=1&customer=<uid>&customerName=<name>.
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillUid = searchParams.get('customer');
  const prefillName = searchParams.get('customerName');
  const prefill = prefillUid !== null && prefillName !== null ? { uid: prefillUid, name: prefillName } : undefined;
  const [creating, setCreating] = useState(searchParams.get('new') === '1' && canManage);

  const params: ListCasesParams = {
    q,
    ...(view === 'mine' ? { owner: 'me' } : {}),
    ...(view === 'overdue' ? { overdue: true } : {}),
  };

  const { data, isFetching, error, fetchNextPage, hasNextPage } = useInfiniteQuery({
    queryKey: ['cases', view, q],
    queryFn: ({ pageParam }) => listCases({ ...params, ...(pageParam !== undefined ? { cursor: pageParam } : {}) }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
  });
  const rows = data?.pages.flatMap((p) => p.items) ?? [];

  const download = async (): Promise<void> => {
    try {
      const csv = await exportCasesCsv(params);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `cases-${view}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t('serviceDesk.exportFailed'));
    }
  };

  const emptyFor: Record<View, string> = {
    mine: t('serviceDesk.emptyMine'),
    overdue: t('serviceDesk.emptyOverdue'),
    open: t('serviceDesk.emptyAll'),
  };

  const overdueTag = (row: ServiceCase): ReactNode =>
    isOverdue(row) ? <Tag color="error">{t('serviceDesk.overdue')}</Tag> : null;

  return (
    <>
      <Flex vertical gap={12}>
        <Segmented<View>
          value={view}
          onChange={setView}
          size="large"
          block
          options={[
            { value: 'mine', label: t('serviceDesk.views.mine') },
            { value: 'overdue', label: t('serviceDesk.views.overdue') },
            { value: 'open', label: t('serviceDesk.views.open') },
          ]}
        />
        {error !== null && (
          <Text type="danger">{error instanceof ApiError ? error.message : t('serviceDesk.loadFailed')}</Text>
        )}
        <ListPage<ServiceCase>
          title={t('serviceDesk.title')}
          searchPlaceholder={t('serviceDesk.search')}
          searchValue={search}
          onSearch={setSearch}
          loading={isFetching}
          emptyDescription={q.length > 0 ? t('serviceDesk.noResults') : emptyFor[view]}
          actions={
            <>
              <Button icon={<DownloadOutlined />} onClick={() => void download()}>
                {t('serviceDesk.exportCsv')}
              </Button>
              {canManage && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  {t('serviceDesk.new')}
                </Button>
              )}
            </>
          }
          columns={[
            { title: t('serviceDesk.col.caseNo'), dataIndex: 'caseNo', width: 180 },
            { title: t('serviceDesk.col.subject'), dataIndex: 'subject' },
            { title: t('serviceDesk.col.customer'), key: 'customer', render: (_: unknown, r) => r.customer.name },
            {
              title: t('serviceDesk.col.status'),
              key: 'status',
              width: 200,
              render: (_: unknown, r) => (
                <Flex gap={4} wrap>
                  <Tag color={statusColour(r.status)}>{statusLabel(t, r.status)}</Tag>
                  {overdueTag(r)}
                </Flex>
              ),
            },
            {
              title: t('serviceDesk.col.severity'),
              dataIndex: 'severity',
              width: 110,
              render: (v: string) => <Tag color={severityColour(v)}>{severityLabel(t, v)}</Tag>,
            },
            { title: t('serviceDesk.col.owner'), key: 'owner', width: 140, render: (_: unknown, r) => r.owner.name },
            {
              title: t('serviceDesk.col.due'),
              dataIndex: 'slaResolveDueAt',
              width: 170,
              render: (v: string, r) => (
                <Text type={r.breached.resolve ? 'danger' : undefined}>{formatDateTime(v)}</Text>
              ),
            },
          ]}
          rows={rows}
          rowKey={(row) => row.uid}
          onRowClick={(row) => {
            void navigate(`/cases/${row.uid}`);
          }}
          hasMore={hasNextPage}
          onLoadMore={() => void fetchNextPage()}
          renderMobileRow={(row) => (
            <Flex vertical gap={4}>
              <Flex justify="space-between" align="center" gap={8}>
                <Text strong>{row.caseNo}</Text>
                <Flex gap={4}>
                  {overdueTag(row)}
                  <Tag color={statusColour(row.status)} style={{ marginInlineEnd: 0 }}>
                    {statusLabel(t, row.status)}
                  </Tag>
                </Flex>
              </Flex>
              <Text>{row.subject}</Text>
              <Text type="secondary">{row.customer.name}</Text>
              <Text type={row.breached.resolve ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                {t('serviceDesk.clock.resolve')} {formatDateTime(row.slaResolveDueAt)}
              </Text>
            </Flex>
          )}
        />
      </Flex>

      <NewCaseDrawer
        open={creating}
        {...(prefill !== undefined ? { customer: prefill } : {})}
        onClose={() => {
          setCreating(false);
          setSearchParams({}, { replace: true });
        }}
        onCreated={(uid, caseNo) => {
          setCreating(false);
          setSearchParams({}, { replace: true });
          void queryClient.invalidateQueries({ queryKey: ['cases'] });
          void message.success(t('serviceDesk.newCase.created', { caseNo }));
          void navigate(`/cases/${uid}`);
        }}
      />
    </>
  );
}
